import React, { createContext, useContext, useEffect, useState } from 'react';

import notificationApi from '@/core/notifications/api/notification-api';
import {
  Topic,
  NotificationChannel as Channel,
  Subscription,
  NotificationApplication,
} from '@/core/notifications/model';

interface NotificationServiceContextValue {
  topics: Topic[];
  channels: Channel[];
  subscriptions: Subscription[];
  applications: NotificationApplication[];
  topicByKey: (key: string) => Topic | undefined;
  subscriptionById: (id: string) => Subscription | undefined;
  applicationById: (id: string) => NotificationApplication | undefined;
  refreshSubscriptions: () => void;
  isLoading: boolean;
}

const NotificationServiceContext = createContext<NotificationServiceContextValue>({
  topics: [],
  channels: [],
  subscriptions: [],
  applications: [],
  topicByKey: () => undefined,
  subscriptionById: () => undefined,
  applicationById: () => undefined,
  refreshSubscriptions: () => {},
  isLoading: true,
});

export function NotificationServiceContextProvider({ children }: React.PropsWithChildren<{}>) {
  const [topics, setTopics] = useState<Topic[]>([]);
  const [channels, setChannels] = useState<Channel[]>([]);
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [applications, setApplications] = useState<NotificationApplication[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      notificationApi.getTopics(),
      notificationApi.getChannels(),
      notificationApi.getSubscriptions(),
      notificationApi.getApplications(),
    ])
      .then(([t, ch, subs, apps]) => {
        setTopics(t);
        setChannels(
          ch.length > 0
            ? ch
            : [{ key: 'in-app', displayName: 'In App', instructions: null, configFields: [] }],
        );
        setSubscriptions(subs);
        setApplications(apps);
      })
      .finally(() => setIsLoading(false));
  }, []);

  const refreshSubscriptions = () => {
    notificationApi.getSubscriptions().then(setSubscriptions);
  };

  const topicByKey = (key: string) => topics.find(t => t.key === key);
  const subscriptionById = (id: string) => subscriptions.find(s => s.id === id);
  const applicationById = (id: string) => applications.find(a => a.id === id);

  return (
    <NotificationServiceContext.Provider
      value={{
        topics,
        channels,
        subscriptions,
        applications,
        topicByKey,
        subscriptionById,
        applicationById,
        refreshSubscriptions,
        isLoading,
      }}
    >
      {children}
    </NotificationServiceContext.Provider>
  );
}

export function useNotificationServiceContext() {
  return useContext(NotificationServiceContext);
}
